import Tooltip from 'rc-tooltip';
import { type FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { IconComponent } from '@/components/IconComponent';
import { ImageComponent } from '@/components/Image';
import {
  ProgressBar,
  Units,
  ValuePosition,
} from '@/components/progressBars/LinearProgressBar';
import { ProgressBarMode } from '@/components/progressBars/LinearProgressBar/styled';
import { useMedia } from '@/hooks';
import { cx } from '@/modules/common/helpers/classNames';
import { useGetCommonStore } from '@/store';
import theme from '@/theme';
import { useCMSLoyaltyProgram } from '../hooks/useCMSLoyaltyProgram';
import { useLoyaltyProgress } from '../hooks/useLoyaltyProgress';
import { ScProgressBarContainer } from './styled';

const overlayStyle = {
  opacity: '1',
  borderRadius: '12px',
  zIndex: 1120,
};

const overlayInnerStyle = {
  background: theme.whiteColor,
  padding: '12px',
  borderRadius: '12px',
  border: 'none',
  color: theme.blackColor,
  fontSize: '14px',
  fontWeight: '400',
  lineHeight: '20px',
  textAlign: 'center' as const,
  boxShadow:
    '0px 0px 8px 0px rgba(0, 0, 0, 0.2), 0px 0px 12px 0px rgba(0, 0, 0, 0.6)',
};

const BORDER_RADIUS_FULL = '24px';
const BORDER_RADIUS_PARTIAL = '24px 0 0 24px';

interface IRetentionTextProps {
  isLastLevel: boolean;
  text: string;
  tooltipText: string;
}

const RetentionText = memo<IRetentionTextProps>(
  ({ isLastLevel, text, tooltipText }) => {
    const { isMobile } = useMedia();

    // For last level: right-aligned, no tooltip
    if (isLastLevel) {
      return (
        <p className="text">
          <span>{text}</span>
        </p>
      );
    }

    // For levels 2-11: with tooltip
    return (
      <Tooltip
        overlay={tooltipText}
        overlayInnerStyle={{
          ...overlayInnerStyle,
          width: isMobile ? '276px' : '306px',
        }}
        overlayStyle={overlayStyle}
        placement="bottomLeft"
        showArrow={false}
        trigger="click"
      >
        <button
          className="text"
          onClick={(e) => e.stopPropagation()}
          type="button"
        >
          <IconComponent name="tooltipIcon" />
          <span>{text}</span>
        </button>
      </Tooltip>
    );
  }
);

RetentionText.displayName = 'RetentionText';

export const Progress: FC = () => {
  const { t } = useTranslation('loyalty');

  const useCommonStore = useGetCommonStore();
  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const loyaltySseState = useCommonStore.use.loyaltySseState();
  const level = loyaltySseState?.level ?? null;

  const {
    points,
    targetAmount,
    lpToSecure,
    isSecured,
    isLastLevel,
    showRetentionGoal,
    retainPercentage,
  } = useLoyaltyProgress();
  const { data } = useCMSLoyaltyProgram();

  const isLoyaltyUser = Boolean(isAuthenticated && level);
  // level.id is 1-based; loyaltySseState.levels[level.id] (0-based) is the next level
  const nextSseLevel = loyaltySseState?.levels[level?.id ?? 0] ?? null;
  const isNextPrizeActivated = !!nextSseLevel?.bonus?.activated;
  // For non-loyalty users on level 1: show empty progress bar
  const displayPoints = isLoyaltyUser ? points : 0;

  const borderRadiusInner = useMemo(
    () =>
      displayPoints >= targetAmount
        ? BORDER_RADIUS_FULL
        : BORDER_RADIUS_PARTIAL,
    [displayPoints, targetAmount]
  );

  const showMilestone = isLoyaltyUser && showRetentionGoal;
  const showRetentionText = isLoyaltyUser && showRetentionGoal;

  const containerClassName = cx(
    isSecured && 'secured',
    isLastLevel && 'last-level'
  );

  const retentionText = useMemo(
    () =>
      isSecured
        ? t('levelSecured')
        : t('remainsToSecure', { value: lpToSecure }),
    [isSecured, lpToSecure, t]
  );

  const tooltipText = useMemo(() => {
    if (!data) return '';
    return isSecured
      ? data.tooltipTextForSecuredLevel
      : data.tooltipTextForNotSecuredLevel;
  }, [isSecured, data]);

  const markerStyle = useMemo(
    () => ({ left: `${retainPercentage}%` }),
    [retainPercentage]
  );

  const mode =
    showRetentionGoal && isSecured
      ? ProgressBarMode.GREEN_WHITE
      : ProgressBarMode.PRIMARY_WHITE;

  return (
    <ScProgressBarContainer className={containerClassName || undefined}>
      <div className="progress-wrapper">
        <ProgressBar
          borderRadius={BORDER_RADIUS_FULL}
          borderRadiusInner={borderRadiusInner}
          from={0}
          height={24}
          mode={mode}
          showTo
          showValue={ValuePosition.CENTER}
          to={targetAmount}
          units={Units.LP}
          value={displayPoints}
          withUnits
        />
        {showMilestone && <div className="retain-marker" style={markerStyle} />}
      </div>
      <div className="bottom">
        {showRetentionText && (
          <RetentionText
            isLastLevel={isLastLevel}
            text={retentionText}
            tooltipText={tooltipText}
          />
        )}
        {!(isLastLevel || isNextPrizeActivated) && (
          <span className="right-aligned">
            <ImageComponent
              alt="gift"
              height={16}
              src="/loyalty/gift.webp"
              width={16}
            />
          </span>
        )}
      </div>
    </ScProgressBarContainer>
  );
};
